import { AutoComplete, Empty, EntitySnippet, Flex, Icon, Input, Search2LineIcon, Spin, message } from 'ui';
import type { InvitationSearchOptionLabel } from '../types';
import { useGetOptionLabelProperties } from './hooks';

type P<T extends InvitationSearchOptionLabel> = {
  disabled?: boolean;
  loading?: boolean;
  onSearchChange: (value: string) => void;
  onSelect: (option: T) => void;
  options: T[];
  search?: string;
};

const InvitationSearchInput = <T extends InvitationSearchOptionLabel>({
  disabled,
  loading,
  onSearchChange,
  onSelect,
  options,
  search,
}: P<T>) => {
  const getOptionLabelProperties = useGetOptionLabelProperties();

  return (
    <AutoComplete
      defaultActiveFirstOption
      disabled={disabled}
      fieldNames={{ label: 'optionLabel' }}
      notFoundContent={
        loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : (
          <Empty description="No data" image={<Icon icon="inbox-line" />} size="small" />
        )
      }
      onSelect={(value) => {
        const selectedOption = options.find((option) => option.value === value);

        if (selectedOption) {
          onSelect(selectedOption);
          onSearchChange('');
        } else {
          message.error('Invalid option');
        }
      }}
      options={options.map((option) => {
        const labelProperties = getOptionLabelProperties(option);
        return {
          label: option.label,
          optionLabel: (
            <EntitySnippet
              avatarProps={{
                fallbackPhoto: labelProperties.fallbackPhoto,
                photo: labelProperties.photo,
              }}
              descriptionProps={{ children: labelProperties.description }}
              titleProps={{ children: labelProperties.title }}
            />
          ),
          value: option.value,
        };
      })}
      showSearch={{ onSearch: onSearchChange }}
      value={search}
    >
      <Input disabled={disabled} prefix={<Search2LineIcon />} value={search} />
    </AutoComplete>
  );
};

export default InvitationSearchInput;
